import type {
  CompleteMicroSegmentationResult,
  MicroUnit
} from '../types/segmentationTypes'
import { isLedgerSafeRetiredSourceUnit } from './ledgerSafeRetiredMaterial'

/**
 * Location inside the legacy chunk-shaped persistence envelope.
 *
 * Ledger Safe editing must not attach semantic meaning to this location. The
 * document-ordered unit stream is authoritative; this locator exists only so
 * an edited unit can be projected back into older persisted records.
 */
export interface LedgerSafeUnitStorageLocation {
  containerIndex: number
  unitIndex: number
}

export interface LedgerSafeWitnessUnitStreamEntry {
  unit: MicroUnit
  storage: LedgerSafeUnitStorageLocation
}

const finitePositionOrLast = (value: unknown): number => {
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : Number.MAX_SAFE_INTEGER
}

export const buildLedgerSafeWitnessUnitStream = (
  segmentationResult: CompleteMicroSegmentationResult | null | undefined
): LedgerSafeWitnessUnitStreamEntry[] => {
  if (!segmentationResult?.chunk_results?.length) {
    return []
  }

  const entries: LedgerSafeWitnessUnitStreamEntry[] = []
  segmentationResult.chunk_results.forEach((container, containerIndex) => {
    const units = Array.isArray(container?.micro_units) ? container.micro_units : []
    units.forEach((unit, unitIndex) => {
      // Source replacement retains ignored/artifact units as zero-width records
      // for provenance. They are not part of the running-text unit stream and
      // must not sit between otherwise adjacent editable boundaries.
      if (isLedgerSafeRetiredSourceUnit(unit)) {
        return
      }
      entries.push({
        unit,
        storage: { containerIndex, unitIndex }
      })
    })
  })

  return entries.sort((left, right) => {
    const startDifference = finitePositionOrLast(left.unit.start_position) - finitePositionOrLast(right.unit.start_position)
    if (startDifference !== 0) {
      return startDifference
    }
    const endDifference = finitePositionOrLast(left.unit.end_position) - finitePositionOrLast(right.unit.end_position)
    if (endDifference !== 0) {
      return endDifference
    }
    const identityDifference = String(left.unit.unit_uuid || left.unit.unit_id || '')
      .localeCompare(String(right.unit.unit_uuid || right.unit.unit_id || ''))
    if (identityDifference !== 0) {
      return identityDifference
    }
    const containerDifference = left.storage.containerIndex - right.storage.containerIndex
    return containerDifference !== 0
      ? containerDifference
      : left.storage.unitIndex - right.storage.unitIndex
  })
}

export const cloneLedgerSafeSegmentationEnvelope = (
  segmentationResult: CompleteMicroSegmentationResult
): CompleteMicroSegmentationResult => ({
  ...segmentationResult,
  id_remap: segmentationResult.id_remap
    ? Object.fromEntries(
      Object.entries(segmentationResult.id_remap).map(([key, values]) => [
        key,
        Array.isArray(values) ? [...values] : values
      ])
    )
    : segmentationResult.id_remap,
  numbers: segmentationResult.numbers
    ? Object.fromEntries(
      Object.entries(segmentationResult.numbers).map(([key, values]) => [
        key,
        Array.isArray(values) ? values.map(value => ({ ...value })) : values
      ])
    )
    : segmentationResult.numbers,
  chunk_results: Array.isArray(segmentationResult.chunk_results)
    ? segmentationResult.chunk_results.map(container => ({
      ...container,
      chunk_position_data: container.chunk_position_data
        ? { ...container.chunk_position_data }
        : container.chunk_position_data,
      micro_units: Array.isArray(container.micro_units)
        ? container.micro_units.map(unit => ({
          ...unit,
          processing_metadata: unit.processing_metadata
            ? { ...unit.processing_metadata }
            : unit.processing_metadata
        }))
        : []
    }))
    : []
})

export const getLedgerSafeStoredUnit = (
  segmentationResult: CompleteMicroSegmentationResult,
  storage: LedgerSafeUnitStorageLocation
): MicroUnit | undefined =>
  segmentationResult.chunk_results?.[storage.containerIndex]?.micro_units?.[storage.unitIndex]

export const getLedgerSafeStorageContainerStart = (
  segmentationResult: CompleteMicroSegmentationResult,
  storage: LedgerSafeUnitStorageLocation
): number => {
  const container = segmentationResult.chunk_results?.[storage.containerIndex]
  const start = Number(
    container?.chunk_position_data?.start_position
    ?? container?.chunk_position_data?.relative_start
    ?? 0
  )
  return Number.isFinite(start) ? start : 0
}

export const removeLedgerSafeStoredUnits = (
  segmentationResult: CompleteMicroSegmentationResult,
  locations: LedgerSafeUnitStorageLocation[]
): void => {
  const unitIndexesByContainer = new Map<number, Set<number>>()
  locations.forEach(({ containerIndex, unitIndex }) => {
    const indexes = unitIndexesByContainer.get(containerIndex) || new Set<number>()
    indexes.add(unitIndex)
    unitIndexesByContainer.set(containerIndex, indexes)
  })

  unitIndexesByContainer.forEach((unitIndexes, containerIndex) => {
    const units = segmentationResult.chunk_results?.[containerIndex]?.micro_units
    if (!Array.isArray(units)) {
      return
    }
    Array.from(unitIndexes)
      .sort((left, right) => right - left)
      .forEach(unitIndex => units.splice(unitIndex, 1))
  })
}

export const removeLedgerSafeUnitIdentitiesFromNumbers = (
  segmentationResult: CompleteMicroSegmentationResult,
  removedUnitUuids: string[]
): void => {
  if (!segmentationResult.numbers || removedUnitUuids.length === 0) {
    return
  }
  const removed = new Set(removedUnitUuids)
  Object.keys(segmentationResult.numbers).forEach(key => {
    segmentationResult.numbers![key] = segmentationResult.numbers![key]
      .filter(entry => !removed.has(String(entry?.unit_uuid || '')))
  })
}

export const refreshLedgerSafeTotalUnitCount = (
  segmentationResult: CompleteMicroSegmentationResult
): void => {
  const totalUnits = (segmentationResult.chunk_results || []).reduce(
    (sum, container) => sum + (Array.isArray(container?.micro_units) ? container.micro_units.length : 0),
    0
  )
  segmentationResult.aggregated_statistics = {
    ...(segmentationResult.aggregated_statistics || {}),
    total_units: totalUnits
  }
}
